import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaStar,
  FaRocket,
  FaEye,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const CorporateVideoProductionInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Corporate Video Production in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Elevate Your Brand</Subheading>}
        heading={
          <Heading>
            Corporate <span tw="text-primary-500">Video Production</span> in
            Dubai
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we deliver professional corporate video
            production services in **Dubai**, creating high-impact videos that
            enhance your brand’s credibility and engage stakeholders. From
            company profiles to training videos, we craft content that drives
            results.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1505236858219-8359eb29e329?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2662&q=80", // Professional video production setup
          "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Team collaboration in office
          "https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Modern corporate environment
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Your Project"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Expert Corporate Video Production in Dubai"
        description="We create videos that align with your corporate goals."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Professional Quality",
            description: "4K videos with polished editing and branding.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted by Brands",
            description: "Serving top companies with proven results.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Delivery",
            description: "Timely production to meet corporate deadlines.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Impactful Storytelling",
            description: "Videos that resonate with your audience.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Tailored Solutions",
            description: "Customized to reflect your brand identity.",
          },
          {
            icon: <FaCheckCircle color="#ff5464" size={40} />,
            title: "Versatile Formats",
            description: "Profiles, promos, training, and event videos.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Corporate Video Production Process"
        description={
          <Description>
            We craft impactful corporate videos with a refined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>Consultation: Align with your brand and objectives.</li>
              <li>Scripting: Develop compelling narratives and storyboards.</li>
              <li>Filming: Use advanced equipment for professional visuals.</li>
              <li>Editing: Enhance with color grading and branded elements.</li>
              <li>
                Delivery: Provide polished videos in your preferred format.
              </li>
              <li>Support: Offer revisions and ongoing assistance.</li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1570913196677-6121448dd763?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Video cameras and equipment
          "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Video editing workspace
          "https://images.unsplash.com/photo-1531884070720-875c7622d4c6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Team shooting a corporate video
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Corporate Video Wins in Dubai"
        description="Our videos have strengthened corporate brands."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Brand Profile",
            description: "Elevated a company’s image with a sleek video.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Stakeholder Engagement",
            description: "Increased reach for a Dubai corporation.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "95% approval from corporate clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready for Professional Videos?</Subheading>}
        heading="Corporate Video Production in Dubai"
        description={
          <Description>
            Partner with MilaajBrandset for professional corporate video
            production in Dubai. Get high-impact videos that reflect your
            brand’s excellence—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1581092335397-9583eb92d232?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Dubai skyline/business district
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80", // Business meeting/presentation
          "https://images.unsplash.com/photo-1621619856624-42fd193a0661?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2674&q=80", // Professional video filming equipment
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
